/**
 * Configuração › Dados da farmácia (só contas de farmácia): razão social, CNAE, AFE, responsável técnico e endereço do
 * estabelecimento; os documentos sanitários (licença sanitária, CRT, AFE) com envio/substituição, situação e validade;
 * as categorias que a farmácia vai vender (cada uma passa pela curadoria da Berytus) com a declaração de escrituração no
 * SNGPC para antimicrobianos e controlados; e as habilitações calculadas. SNGPC não é documento exigido: o que já foi
 * enviado fica como anexo opcional. A Berytus analisa cada documento.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, dateTime } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { can } from '@berytus/contracts';
import { formatCnpj } from '@berytus/core';

type State = 'valid' | 'renewal' | 'expired' | 'pending' | 'rejected' | 'missing';
interface Doc { id: string; kind: string; label: string; number: string | null; issuedAt: string | null; expiresAt: string | null; renewalProtocol: boolean;
  fileName: string; status: string; rejectReason: string | null; createdAt: string; createdBy: string | null; reviewedAt: string | null }
interface Address { street: string; number: string; complement?: string; district: string; city: string; state: string; zip: string }
interface Data {
  profile: { legalName: string | null; cnpj: string | null; cnae: string | null; afeNumber: string | null; rtName: string | null; rtCrf: string | null; rtCrfUf: string | null;
    establishmentAddress: Partial<Address> | null; complete: boolean };
  kinds: { kind: string; label: string; state: State; expiresAt: string | null; current: Doc | null }[];
  history: Doc[];
  habilitations: { level1: boolean; level1Docs: boolean; level2: boolean; sngpcDeclared: boolean; waiver: boolean; waiverEndsAt: string | null; missing: string[] };
  scope: { declared: boolean; items: string[]; labels: string[]; required: { kind: string; label: string; ok: boolean }[]; prescriptionValidation: { required: boolean; confirmedAt: string | null };
    sngpcDeclaration: { text: string; required: boolean; declaredAt: string | null; declaredBy: string | null };
    categories: { category: string; label: string; status: string; statusLabel: string; reason: string | null }[]; pending: string[] };
}
const STATE: Record<State, { label: string; tone: string }> = {
  valid: { label: 'Válido', tone: 'ok' }, renewal: { label: 'Renovação em análise (vale até a decisão)', tone: 'info' }, expired: { label: 'Vencido', tone: 'err' },
  pending: { label: 'Em análise', tone: 'info' }, rejected: { label: 'Recusado', tone: 'err' }, missing: { label: 'Não enviado', tone: 'warn' },
};
const STATUS: Record<string, string> = { pending_review: 'em análise', approved: 'aprovado', rejected: 'recusado', expired: 'vencido' };
const br = (d: string | null) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');
const cnaeFmt = (c: string | null) => (c && c.length === 7 ? `${c.slice(0, 4)}-${c[4]}/${c.slice(5)}` : c ?? '');
const toBase64 = (f: File) => new Promise<string>((ok, bad) => { const r = new FileReader(); r.onload = () => ok(String(r.result).split(',')[1] ?? ''); r.onerror = bad; r.readAsDataURL(f); });

export function PharmacyPage() {
  const me = useMe();
  const canEdit = can(me.tenant.access, 'settings', 'write');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['pharmacy'], queryFn: () => api<Data>('/pharmacy'), enabled: me.tenant.vertical === 'pharmacy' });
  if (me.tenant.vertical !== 'pharmacy') return <div className="empty"><b>Conta que não é de farmácia</b>Os dados da farmácia valem só para contas de farmácia.</div>;
  const d = q.data;
  const h = d?.habilitations;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Dados da farmácia</h1>
          <p>Cadastro do estabelecimento e os documentos sanitários. A Berytus confere cada documento; as habilitações saem dos documentos aprovados e dentro da validade.</p>
        </div>
      </div>
      {q.error ? <ErrorBox error={q.error} /> : !d || !h ? <Loading /> : (
        <div className="stack-y">
          <Scope d={d} canEdit={canEdit} onDone={() => qc.invalidateQueries({ queryKey: ['pharmacy'] })} />
          <Controlled canEdit={canEdit} />
          <section className="kpis" aria-label="Habilitações">
            <div className={`kpi${h.level1 ? '' : ' alert'}`}>
              <span className="k-label">Nível 1 (medicamentos)</span>
              <span className="k-value">{h.level1 ? 'Habilitada' : 'Pendente'}</span>
              <span className="k-note">Licença sanitária, CRT e AFE aprovados e vigentes{h.waiver && !h.level1Docs ? ` · coberto pela liberação provisória até ${dateTime(h.waiverEndsAt)}` : ''}</span>
            </div>
            <div className={`kpi${h.level2 ? '' : ' alert'}`}>
              <span className="k-label">Antimicrobianos e controlados</span>
              <span className="k-value">{h.level2 ? 'Habilitada' : 'Pendente'}</span>
              <span className="k-note">Licença sanitária, CRT e AFE aprovados + declaração de escrituração no SNGPC. A liberação provisória não vale aqui.</span>
            </div>
          </section>

          <Profile d={d} canEdit={canEdit} onDone={() => qc.invalidateQueries({ queryKey: ['pharmacy'] })} />

          <section className="panel flush">
            <div className="panel-head"><h2>Documentos</h2><span className="small muted">PDF, JPG ou PNG até 10 MB. Cada envio fica no histórico.</span></div>
            {d.kinds.filter((k) => k.kind !== 'sngpc' || k.current).map((k) => <DocRow key={k.kind} k={k} canEdit={canEdit} onDone={() => { qc.invalidateQueries({ queryKey: ['pharmacy'] }); qc.invalidateQueries({ queryKey: ['general'] }); }} />)}
          </section>

          <details className="panel">
            <summary><b>Histórico de envios</b> <span className="small muted">({d.history.length})</span></summary>
            <table className="simple" style={{ marginTop: 10 }}>
              <thead><tr><th>Enviado</th><th>Documento</th><th>Número</th><th>Validade</th><th>Situação</th><th /></tr></thead>
              <tbody>
                {d.history.map((x) => (
                  <tr key={x.id}>
                    <td className="small">{dateTime(x.createdAt)}{x.createdBy ? ` · ${x.createdBy}` : ''}</td>
                    <td>{x.label}{x.renewalProtocol ? ' (protocolo de renovação)' : ''}</td>
                    <td className="mono small">{x.number ?? '—'}</td>
                    <td>{br(x.expiresAt)}</td>
                    <td>{STATUS[x.status] ?? x.status}{x.rejectReason && <div className="small" style={{ color: 'var(--err)' }}>{x.rejectReason}</div>}</td>
                    <td><a className="small" href={`/api/v1/pharmacy/documents/${x.id}/file`} target="_blank" rel="noreferrer">Ver arquivo</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}
    </>
  );
}

const SCOPES: [string, string][] = [['mip', 'MIP (isentos de prescrição)'], ['prescription', 'Prescritos sem retenção de receita'], ['antimicrobial', 'Antimicrobianos'],
  ['thermolabile', 'Termolábeis (refrigerados)'], ['controlled', 'Controlados (Portaria 344)']];
const SNGPC_SCOPES = ['antimicrobial', 'controlled'];
const CAT_TONE: Record<string, string> = { pending: 'info', approved: 'ok', rejected: 'err', revoked: 'off' };
/**
 * O que a farmácia vai vender e o que isso exige: documentos, a declaração de escrituração no SNGPC (antimicrobianos e
 * controlados), o fluxo de validação de receita e a aprovação de cada categoria pela curadoria da Berytus.
 */
function Scope({ d, canEdit, onDone }: { d: Data; canEdit: boolean; onDone: () => void }) {
  const [items, setItems] = useState<string[]>(d.scope.items);
  const [validation, setValidation] = useState(!!d.scope.prescriptionValidation.confirmedAt);
  const [sngpc, setSngpc] = useState(!!d.scope.sngpcDeclaration.declaredAt);
  const save = useMutation({ mutationFn: () => api('/pharmacy/scope', { method: 'PUT', json: { scope: items, prescriptionValidation: validation, sngpcDeclaration: sngpc } }), onSuccess: onDone });
  const needsValidation = items.includes('prescription') || items.includes('antimicrobial');
  const needsSngpc = items.some((x) => SNGPC_SCOPES.includes(x));
  const cat = (k: string) => d.scope.categories.find((c) => c.category === k);
  return (
    <section className="panel stack-y" style={{ gap: 8 }}>
      <h2 style={{ margin: 0 }}>O que a farmácia vai vender</h2>
      <p className="small muted" style={{ margin: 0 }}>Cada categoria (fora o MIP) passa pela curadoria da Berytus, que confere a licença sanitária e a AFE. Até os documentos
        serem aprovados, os anúncios de medicamento podem ser criados, mas ficam pausados (salvo liberação provisória da Berytus). Controlados dependem também da
        habilitação do canal pela Berytus e do pedido em "Controlados (Portaria 344)".</p>
      <div className="small" style={{ display: 'grid', gap: 4 }}>{SCOPES.map(([k, l]) => {
        const c = cat(k);
        return (
          <label key={k}><input type="checkbox" className="check" disabled={!canEdit} checked={items.includes(k)}
            onChange={(e) => setItems(e.target.checked ? [...items, k] : items.filter((x) => x !== k))} /> {l}
            {c && d.scope.items.includes(k) && <> <Tag tone={CAT_TONE[c.status] ?? 'off'}>{c.statusLabel}</Tag>{c.reason && <span className="muted"> · {c.reason}</span>}</>}
          </label>
        );
      })}</div>
      {needsSngpc && (
        <label className="small"><input type="checkbox" className="check" disabled={!canEdit} checked={sngpc} onChange={(e) => setSngpc(e.target.checked)} />
          {' '}{d.scope.sngpcDeclaration.text}
          {d.scope.sngpcDeclaration.declaredAt && <span className="muted"> (marcada em {dateTime(d.scope.sngpcDeclaration.declaredAt)}{d.scope.sngpcDeclaration.declaredBy ? ` por ${d.scope.sngpcDeclaration.declaredBy}` : ''})</span>}
        </label>
      )}
      {needsValidation && <label className="small"><input type="checkbox" className="check" disabled={!canEdit} checked={validation} onChange={(e) => setValidation(e.target.checked)} />
        {' '}Confirmo que todo pedido com receita é validado por um farmacêutico (CRF) da conta antes da nota e da etiqueta</label>}
      {d.scope.declared && (d.scope.pending.length
        ? <div className="notice warn small">Falta: {d.scope.pending.join(' · ')}</div>
        : <div className="small" style={{ color: 'var(--ok)' }}>Documentos, declarações e categorias em dia para o que foi declarado.</div>)}
      {save.error && <ErrorBox error={save.error} />}
      {canEdit && <div><button type="button" className="btn sm primary" disabled={save.isPending || (needsSngpc && !sngpc)} onClick={() => save.mutate()}>Salvar</button>
        {needsSngpc && !sngpc && <span className="small muted"> Marque a declaração do SNGPC para pedir antimicrobianos ou controlados.</span>}</div>}
    </section>
  );
}

function Profile({ d, canEdit, onDone }: { d: Data; canEdit: boolean; onDone: () => void }) {
  const p = d.profile;
  const a = p.establishmentAddress ?? {};
  const [f, setF] = useState({
    legalName: p.legalName ?? '', cnae: cnaeFmt(p.cnae), afeNumber: p.afeNumber ?? '', rtName: p.rtName ?? '', rtCrf: p.rtCrf ?? '', rtCrfUf: p.rtCrfUf ?? '',
    street: a.street ?? '', number: a.number ?? '', complement: a.complement ?? '', district: a.district ?? '', city: a.city ?? '', state: a.state ?? '', zip: a.zip ?? '',
  });
  const save = useMutation({
    mutationFn: () => api('/pharmacy/profile', { method: 'PUT', json: {
      legalName: f.legalName, cnae: f.cnae, afeNumber: f.afeNumber, rtName: f.rtName, rtCrf: f.rtCrf, rtCrfUf: f.rtCrfUf,
      establishmentAddress: { street: f.street, number: f.number, complement: f.complement, district: f.district, city: f.city, state: f.state, zip: f.zip },
    } }),
    onSuccess: onDone,
  });
  const field = (k: keyof typeof f, label: string, w?: number, hint?: string) => (
    <label className="small" style={{ display: 'grid', gap: 4, minWidth: w ?? 160, flex: w ? `0 0 ${w}px` : '1 1 220px' }}>{label}
      <input className="input" value={f[k]} disabled={!canEdit} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
      {hint && <span className="muted" style={{ fontSize: 12 }}>{hint}</span>}
    </label>
  );
  return (
    <form className="panel stack-y" style={{ gap: 12 }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Estabelecimento</h2>
        {!p.complete && <Tag tone="warn">cadastro incompleto</Tag>}
        <span className="small muted">CNPJ {p.cnpj ? formatCnpj(p.cnpj) : '—'}</span>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {field('legalName', 'Razão social')}
        {field('cnae', 'CNAE', 150, '4771-7/01, /02 ou /03')}
        {field('afeNumber', 'Nº da AFE', 170)}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {field('rtName', 'Responsável técnico')}
        {field('rtCrf', 'CRF', 130)}
        {field('rtCrfUf', 'UF do CRF', 90)}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {field('street', 'Endereço')}{field('number', 'Número', 100)}{field('complement', 'Complemento', 160)}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {field('district', 'Bairro')}{field('city', 'Cidade')}{field('state', 'UF', 80)}{field('zip', 'CEP', 120)}
      </div>
      {save.error && <ErrorBox error={save.error} />}
      {canEdit && <div><button type="submit" className="btn primary sm" disabled={save.isPending}>{save.isSuccess ? 'Salvo' : 'Salvar'}</button></div>}
    </form>
  );
}

function DocRow({ k, canEdit, onDone }: { k: Data['kinds'][number]; canEdit: boolean; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ number: '', issuedAt: '', expiresAt: '', renewal: false, noExpiry: false, file: null as File | null });
  const send = useMutation({
    mutationFn: async () => {
      if (!f.file) throw new Error('Escolha o arquivo.');
      if (f.file.size > 10 * 1024 * 1024) throw new Error('Arquivo acima de 10 MB.');
      return api('/pharmacy/documents', { method: 'POST', json: {
        kind: k.kind, number: f.number || undefined, issuedAt: f.issuedAt || undefined, expiresAt: f.noExpiry ? undefined : f.expiresAt || undefined, renewalProtocol: f.renewal, noExpiry: f.noExpiry,
        fileName: f.file.name, contentType: f.file.type, base64: await toBase64(f.file),
      } });
    },
    onSuccess: () => { setOpen(false); setF({ number: '', issuedAt: '', expiresAt: '', renewal: false, noExpiry: false, file: null }); onDone(); },
  });
  const st = STATE[k.state];
  return (
    <div className="action-row" style={{ alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <b>{k.label}</b> <Tag tone={st.tone}>{st.label}</Tag>
        <div className="small muted">
          {k.expiresAt ? `Validade ${br(k.expiresAt)}` : k.state === 'missing' ? (k.kind === 'sngpc' || k.kind === 'outro' ? 'Opcional' : 'Necessário para medicamentos') : ''}
          {k.current?.rejectReason && k.state === 'rejected' && <span style={{ color: 'var(--err)' }}> · Motivo: {k.current.rejectReason}</span>}
        </div>
        {open && (
          <form className="stack-y" style={{ gap: 8, marginTop: 10 }} onSubmit={(e) => { e.preventDefault(); send.mutate(); }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}>
              <label className="small" style={{ display: 'grid', gap: 4 }}>Número<input className="input" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} /></label>
              <label className="small" style={{ display: 'grid', gap: 4 }}>Emissão<input className="input" type="date" value={f.issuedAt} onChange={(e) => setF({ ...f, issuedAt: e.target.value })} /></label>
              <label className="small" style={{ display: 'grid', gap: 4 }}>Validade<input className="input" type="date" required={!f.renewal && !f.noExpiry && k.kind !== 'outro'} disabled={f.noExpiry} value={f.expiresAt} onChange={(e) => setF({ ...f, expiresAt: e.target.value })} /></label>
              <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" className="check" checked={f.noExpiry} onChange={(e) => setF({ ...f, noExpiry: e.target.checked })} /> Sem vencimento
              </label>
              <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" className="check" checked={f.renewal} onChange={(e) => setF({ ...f, renewal: e.target.checked })} /> É o protocolo de renovação
              </label>
            </div>
            <input type="file" accept="application/pdf,image/jpeg,image/png" required onChange={(e) => setF({ ...f, file: e.target.files?.[0] ?? null })} />
            {send.error && <ErrorBox error={send.error} />}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="btn primary sm" disabled={send.isPending}>{send.isPending ? 'Enviando…' : 'Enviar para análise'}</button>
              <button type="button" className="btn sm quiet" onClick={() => setOpen(false)}>Cancelar</button>
            </div>
          </form>
        )}
      </div>
      {k.current && <a className="small" href={`/api/v1/pharmacy/documents/${k.current.id}/file`} target="_blank" rel="noreferrer">Ver arquivo</a>}
      {canEdit && !open && <button type="button" className="btn sm" onClick={() => setOpen(true)}>{k.current ? 'Substituir' : 'Enviar'}</button>}
    </div>
  );
}

const CHN: Record<string, string> = { mercadolivre: 'Mercado Livre', shopee: 'Shopee', ifood: 'iFood', '99food': '99Food', amazon: 'Amazon' };

/**
 * Controlados (Portaria 344): só aparece quando a Berytus habilitou algum canal; mostra só os canais e as listas
 * habilitados. O pedido exige licença sanitária, CRT e AFE aprovados, a declaração de escrituração no SNGPC e "Controlados"
 * marcado no escopo; passa pela curadoria da Berytus (que também exige a categoria aprovada).
 */
function Controlled({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['pharmacy', 'controlled'], queryFn: () => api<{ available: { channel: string; lists: string[] }[]; ready: boolean; missing: string[];
    requests: { id: string; channel: string; lists: string[]; status: string; createdAt: string; reviewNote: string | null }[] }>('/pharmacy/controlled') });
  const [sel, setSel] = useState<{ channel: string; lists: string[] }>({ channel: '', lists: [] });
  const send = useMutation({ mutationFn: () => api('/pharmacy/controlled/requests', { method: 'POST', json: sel }), onSuccess: () => { setSel({ channel: '', lists: [] }); qc.invalidateQueries({ queryKey: ['pharmacy', 'controlled'] }); } });
  if (!q.data?.available.length) return null;
  const lists = q.data.available.find((a) => a.channel === sel.channel)?.lists ?? [];
  return (
    <section className="panel">
      <div className="panel-head"><h2>Controlados (Portaria 344)</h2></div>
      <p className="small muted">A Berytus habilitou os canais e listas abaixo. Para vender, peça a aprovação (licença sanitária, CRT e AFE aprovados, declaração de
        escrituração no SNGPC e a categoria "Controlados" aprovada pela curadoria) e tenha o fluxo de receita no canal.</p>
      {!q.data.ready && <div className="notice warn small">Antes de pedir, falta: {q.data.missing.join('; ')}.</div>}
      {!!q.data.requests.length && (
        <table className="simple">
          <thead><tr><th>Canal</th><th>Listas</th><th>Situação</th></tr></thead>
          <tbody>{q.data.requests.map((r) => <tr key={r.id}><td>{CHN[r.channel] ?? r.channel}</td><td>{r.lists.join(' ')}</td>
            <td><Tag tone={r.status === 'approved' ? 'ok' : r.status === 'pending' ? 'warn' : 'off'}>{r.status === 'approved' ? 'aprovado' : r.status === 'pending' ? 'em análise' : r.status === 'rejected' ? 'recusado' : 'revogado'}</Tag>
              {r.reviewNote && <div className="small muted">{r.reviewNote}</div>}</td></tr>)}</tbody>
        </table>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
          <select className="select" value={sel.channel} onChange={(e) => setSel({ channel: e.target.value, lists: [] })}>
            <option value="">Canal…</option>{q.data.available.map((a) => <option key={a.channel} value={a.channel}>{CHN[a.channel] ?? a.channel}</option>)}</select>
          {lists.map((l) => <label key={l} className="small"><input type="checkbox" checked={sel.lists.includes(l)} onChange={(e) => setSel({ ...sel, lists: e.target.checked ? [...sel.lists, l] : sel.lists.filter((x) => x !== l) })} /> {l}</label>)}
          <button type="button" className="btn sm primary" disabled={!sel.channel || !sel.lists.length || send.isPending || !q.data.ready} onClick={() => send.mutate()}>Pedir aprovação</button>
        </div>
      )}
      {send.error && <ErrorBox error={send.error} />}
    </section>
  );
}
